import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import { apiFetch, apiDownload } from '../../lib/api';
import { Candidato, StatoCandidatura, StoricoCandidatura, CommentoCandidato } from '../../types';
import '../../styles/ats.css';

const STATI_ORDINE: StatoCandidatura[] = [
  'RICEVUTO','SCREENING','COLLOQUIO_1','COLLOQUIO_2','OFFERTA','ASSUNTO','RIFIUTATO',
];

const STATO_LABEL: Record<StatoCandidatura, string> = {
  RICEVUTO:    'Ricevuto',
  SCREENING:   'Screening',
  COLLOQUIO_1: 'Colloquio 1',
  COLLOQUIO_2: 'Colloquio 2',
  OFFERTA:     'Offerta',
  ASSUNTO:     'Assunto',
  RIFIUTATO:   'Rifiutato',
};

const STATO_BADGE: Record<StatoCandidatura, string> = {
  RICEVUTO:    'badge-slate',
  SCREENING:   'badge-blue',
  COLLOQUIO_1: 'badge-yellow',
  COLLOQUIO_2: 'badge-orange',
  OFFERTA:     'badge-green',
  ASSUNTO:     'badge-green',
  RIFIUTATO:   'badge-red',
};

export default function SchedaCandidato() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [candidato,    setCandidato]    = useState<Candidato | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  /* Cambia stato */
  const [showStato,    setShowStato]    = useState(false);
  const [nuovoStato,   setNuovoStato]   = useState<StatoCandidatura>('SCREENING');
  const [noteStato,    setNoteStato]    = useState('');
  const [savingStato,  setSavingStato]  = useState(false);
  const [statoError,   setStatoError]   = useState('');

  /* Commento */
  const [testo,        setTesto]        = useState('');
  const [savingCom,    setSavingCom]    = useState(false);
  const [comError,     setComError]     = useState('');

  /* Download CV */
  const [downloading,  setDownloading]  = useState(false);

  const load = async () => {
    const data = await apiFetch<{ candidato: Candidato }>(`/api/ats/candidati/${id}`);
    setCandidato(data.candidato);
  };

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const handleCambiaStato = async () => {
    setSavingStato(true);
    setStatoError('');
    try {
      const res = await apiFetch<{ candidato: Candidato; assunto?: boolean; prefillDipendente?: any }>(
        `/api/ats/candidati/${id}/stato`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stato: nuovoStato, note: noteStato.trim() || null }),
        }
      );
      setCandidato(res.candidato);
      setShowStato(false);
      setNoteStato('');
      if (res.assunto && res.prefillDipendente) {
        const ok = window.confirm(
          `${res.candidato.nome} ${res.candidato.cognome} è stato marcato come Assunto.\n\nVuoi creare subito la scheda dipendente?`
        );
        if (ok) {
          navigate('/dipendenti/nuovo', { state: { prefill: res.prefillDipendente } });
        }
      }
    } catch (e: any) {
      setStatoError(e.message);
    } finally {
      setSavingStato(false);
    }
  };

  const handleAddCommento = async () => {
    if (!testo.trim()) return;
    setSavingCom(true);
    setComError('');
    try {
      await apiFetch(`/api/ats/candidati/${id}/commenti`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testo: testo.trim() }),
      });
      setTesto('');
      await load();
    } catch (e: any) {
      setComError(e.message);
    } finally {
      setSavingCom(false);
    }
  };

  const handleDownloadCv = async () => {
    if (!candidato?.pathCvGcs) return;
    setDownloading(true);
    try {
      const filename = candidato.pathCvGcs.split('/').pop() ?? 'cv.pdf';
      await apiDownload(`/api/ats/candidati/${id}/cv`, filename);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) return <Layout title="Candidato"><div className="inline-loading">Caricamento…</div></Layout>;
  if (error || !candidato) return <Layout title="Candidato"><div className="inline-error">{error || 'Non trovato'}</div></Layout>;

  return (
    <Layout title={`${candidato.cognome} ${candidato.nome}`}>
      {/* Header */}
      <BopBackLink to={`/ats/posizioni/${candidato.posizioneId}`} label="Torna alla posizione" />
      <div className="ats-header">
        <div className="ats-header-spacer" />
        <span className={`badge ${STATO_BADGE[candidato.statoCandidatura]}`}>
          {STATO_LABEL[candidato.statoCandidatura]}
        </span>
        <button className="btn btn-primary" onClick={() => { setNuovoStato(candidato.statoCandidatura); setShowStato(true); }}>
          Cambia stato
        </button>
        {candidato.statoCandidatura === 'ASSUNTO' && !candidato.dipendenteId && (
          <button
            className="ats-btn-assunto"
            onClick={() => navigate('/dipendenti/nuovo', {
              state: {
                prefill: {
                  nome:      candidato.nome,
                  cognome:   candidato.cognome,
                  email:     candidato.email,
                  telefono:  candidato.telefono,
                  candidatoId: candidato.id,
                },
              },
            })}
          >
            + Crea Dipendente
          </button>
        )}
        {candidato.dipendenteId && (
          <button className="btn btn-ghost" onClick={() => navigate(`/dipendenti/${candidato.dipendenteId}`)}>
            Vai alla scheda →
          </button>
        )}
      </div>

      <div className="ats-layout-grid">
        {/* Colonna sinistra: dati + CV */}
        <div>
          <div className="card ats-card-inner">
            <h4 className="ats-card-title">Dati candidato</h4>

            <div className="ats-data-grid">
              <div>
                <div className="ats-data-label">Nome</div>
                <div className="ats-data-value">{candidato.nome}</div>
              </div>
              <div>
                <div className="ats-data-label">Cognome</div>
                <div className="ats-data-value">{candidato.cognome}</div>
              </div>
              <div>
                <div className="ats-data-label">Email</div>
                <div>{candidato.email}</div>
              </div>
              <div>
                <div className="ats-data-label">Telefono</div>
                <div>{candidato.telefono || '—'}</div>
              </div>
              {candidato.posizioneTitolo && (
                <div className="ats-data-full">
                  <div className="ats-data-label">Posizione</div>
                  <div>{candidato.posizioneTitolo}</div>
                </div>
              )}
              {candidato.responsabileNome && (
                <div className="ats-data-full">
                  <div className="ats-data-label">Responsabile</div>
                  <div>{candidato.responsabileNome}</div>
                </div>
              )}
              {candidato.note && (
                <div className="ats-data-full">
                  <div className="ats-data-label">Note</div>
                  <div className="ats-note-text">{candidato.note}</div>
                </div>
              )}
            </div>

            {candidato.pathCvGcs && (
              <div className="ats-divider">
                <button className="btn btn-ghost" onClick={handleDownloadCv} disabled={downloading}>
                  {downloading ? 'Download…' : '⬇ Scarica CV'}
                </button>
              </div>
            )}
          </div>

          {/* Storico stati */}
          <div className="card ats-card-inner">
            <h4 className="ats-card-title">Storico stati</h4>
            {(candidato.storicoStati ?? []).length === 0 ? (
              <div className="ats-storico-empty">Nessun cambio di stato registrato.</div>
            ) : (
              <div className="ats-storico-list">
                {[...(candidato.storicoStati ?? [])].reverse().map((s: StoricoCandidatura) => (
                  <div key={s.id} className="ats-storico-item">
                    <div className="ats-storico-dot" />
                    <div>
                      <div>
                        {s.statoPrecedente ? (
                          <><span className="ats-storico-label-muted">{STATO_LABEL[s.statoPrecedente]}</span> → <strong>{STATO_LABEL[s.statoNuovo]}</strong></>
                        ) : (
                          <strong>{STATO_LABEL[s.statoNuovo]}</strong>
                        )}
                        {s.cambiatoDaNome && <span className="ats-storico-label-muted"> da {s.cambiatoDaNome}</span>}
                      </div>
                      <div className="ats-storico-timestamp">{fmtDate(s.cambiatoAt)}</div>
                      {s.note && <div className="ats-storico-note">{s.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Colonna destra: commenti */}
        <div className="card ats-card-inner">
          <h4 className="ats-card-title">Commenti</h4>

          {/* Aggiungi commento */}
          <div className="ats-modal-field">
            <textarea
              className="bop-input ats-commenti-textarea"
              rows={3}
              placeholder="Aggiungi un commento…"
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
            />
            {comError && <div className="inline-error ats-commenti-textarea">{comError}</div>}
            <button className="btn btn-primary" onClick={handleAddCommento} disabled={savingCom || !testo.trim()}>
              {savingCom ? 'Invio…' : 'Aggiungi commento'}
            </button>
          </div>

          <div className="ats-commenti-divider">
            {(candidato.commenti ?? []).length === 0 ? (
              <div className="ats-storico-empty">Nessun commento.</div>
            ) : (
              <div className="ats-commenti-list">
                {[...(candidato.commenti ?? [])].reverse().map((c: CommentoCandidato) => (
                  <div key={c.id}>
                    <div className="ats-commento-header">
                      <strong className="ats-commento-author">{c.autoreNome ?? 'Utente'}</strong>
                      <span className="ats-commento-timestamp">{fmtDate(c.createdAt)}</span>
                    </div>
                    <div className="ats-commento-text">
                      {c.testo}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modale cambia stato */}
      {showStato && (
        <div className="modal-backdrop" onClick={() => !savingStato && setShowStato(false)}>
          <div className="modal ats-modal-width" onClick={(e) => e.stopPropagation()}>
            <h3 className="ats-modal-title">Cambia stato candidatura</h3>
            {statoError && <div className="inline-error ats-modal-error">{statoError}</div>}

            <div className="ats-modal-field">
              <label className="form-label">Nuovo stato</label>
              <select className="bop-select" value={nuovoStato} onChange={(e) => setNuovoStato(e.target.value as StatoCandidatura)}>
                {STATI_ORDINE.map((s) => (
                  <option key={s} value={s}>{STATO_LABEL[s]}</option>
                ))}
              </select>
            </div>

            <div className="ats-modal-note-field">
              <label className="form-label">Note (opzionale)</label>
              <textarea className="bop-input" rows={2} value={noteStato} onChange={(e) => setNoteStato(e.target.value)} placeholder="Es. colloquio superato con ottimo feedback…" />
            </div>

            {nuovoStato === 'ASSUNTO' && (
              <div className="ats-modal-alert">
                Marcando come "Assunto" potrai creare subito la scheda dipendente.
              </div>
            )}

            <div className="ats-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowStato(false)} disabled={savingStato}>Annulla</button>
              <button className="btn btn-primary" onClick={handleCambiaStato} disabled={savingStato}>
                {savingStato ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
